import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Calendar, ChevronRight, ChevronLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Language } from '../App';
interface CurrentIssueProps {
  language: Language;
}
export function CurrentIssue({
  language
}: CurrentIssueProps) {
  const isPashto = language === 'pashto';
  const categories = isPashto ? ['مقالې', 'شاعري', 'علمي', 'سرمقالې'] : ['Articles', 'Poetry', 'Academic', 'Editorials'];
  const previousIssues = [{
    nameEn: 'Mar – Apr 2026',
    namePs: 'مارچ – اپریل ۲۰۲۶',
    volEn: 'Vol 21, Iss 2',
    volPs: 'ټوک ۲۱، ګڼه ۲',
    cover: 'https://cdn.magicpatterns.com/uploads/sR4i2r4S1futQxXXFLun8U/Mar-April_26.jpg',
    link: '/mar-apr-2026'
  }, {
    nameEn: 'Jan – Feb 2026',
    namePs: 'جنوري – فبروري ۲۰۲۶',
    volEn: 'Vol 21, Iss 1',
    volPs: 'ټوک ۲۱، ګڼه ۱',
    cover: 'https://cdn.magicpatterns.com/uploads/nkxSFVNX8PortnXbKmnDDH/image.png',
    link: '/jan-feb-2026'
  }];
  return <section className="py-10 md:py-14 bg-gray-50 px-6 overflow-hidden" data-id="element-75">
      <div className="max-w-5xl mx-auto" data-id="element-76">
        <AnimatePresence mode="wait" data-id="element-77">
          <motion.div key={language} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.4
        }} data-id="element-78">
            {/* Section Header */}
            <div className="mb-8 md:mb-10 text-start" data-id="element-79">
              <h2 className={`text-3xl md:text-4xl font-bold text-pasoon-dark inline-block relative ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-80">
                {isPashto ? 'اوسنۍ ګڼه' : 'Current Issue'}
                <span className="absolute -bottom-3 start-0 w-1/2 h-1 bg-pasoon-red rounded-full" data-id="element-81"></span>
              </h2>
            </div>

            {/* Featured Issue Card */}
            <div className="bg-white rounded-2xl md:rounded-[2rem] shadow-xl shadow-gray-200/50 overflow-hidden flex flex-col md:flex-row mb-10" data-id="element-82">
              {/* Cover Image */}
              <div className="w-full md:w-2/5 lg:w-1/3 min-h-[300px] md:min-h-full relative overflow-hidden group bg-gradient-to-br from-green-50 to-gray-100" data-id="element-83">
                <img src="https://cdn.magicpatterns.com/uploads/dpcJ11jnpAvCh6Q2HiDXDa/Front.jpg" alt={isPashto ? 'پاڅون مۍ – جون ۲۰۲۶' : 'Pasoon May – June 2026'} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-84" />
              </div>

              {/* Content */}
              <div className="w-full md:w-3/5 lg:w-2/3 p-8 md:p-12 flex flex-col justify-center text-start" data-id="element-85">
                <h3 className={`text-2xl md:text-3xl font-bold text-pasoon-dark mb-4 leading-tight ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-86">
                  {isPashto ? 'مۍ – جون ۲۰۲۶ ګڼه اوس شتون لري' : 'May – June 2026 Issue Now Available'}
                </h3>

                <p className={`text-gray-600 text-base md:text-lg mb-8 ${isPashto ? 'font-pashto leading-loose' : 'leading-relaxed'}`} data-id="element-87">
                  {isPashto ? 'دا ګڼه د "په پښتو لیکنه کې توپیرونه" په اړه ځانګړې مقاله، د اسلامیه کالج پېښور علمي او کلتوري مرکز یادونه، شاعري او علمي څیړنې لري.' : 'This issue features a special article on "Differences in Pashto Writing," a tribute to Islamia College Peshawar as an academic and cultural centre, along with poetry and scholarly research.'}
                </p>

                {/* Categories */}
                <div className="mb-8 -mx-8 px-8 md:mx-0 md:px-0 overflow-x-auto scrollbar-hide" data-id="element-88">
                  <div className="flex gap-3 w-max" data-id="element-89">
                    {categories.map((cat, idx) => <span key={idx} className={`px-4 py-2 rounded-full bg-red-50 text-pasoon-red text-sm font-medium whitespace-nowrap border border-red-100 ${isPashto ? 'font-pashto' : ''}`} data-id="element-90">
                        {cat}
                      </span>)}
                  </div>
                </div>

                {/* CTA */}
                <div data-id="element-91">
                  <Link to="/current-issue" className={`inline-flex items-center justify-center gap-2 px-8 py-4 bg-pasoon-red text-white rounded-full font-medium text-lg hover:bg-red-800 transition-all shadow-lg shadow-red-900/20 hover:shadow-red-900/30 hover:-translate-y-0.5 active:translate-y-0 w-full sm:w-auto ${isPashto ? 'font-pashto' : 'font-sans'}`} data-id="element-92">
                    <BookOpen className="w-5 h-5" data-id="element-93" />
                    {isPashto ? 'بشپړه ګڼه ولولئ' : 'Read Full Issue'}
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>;
}